// PopQuizButton — instructor-only "POP QUIZ NOW" button.
// Mounted inside the LectureView presenter. On click: pick class, spin up a 30-sec ambush game
// for that class, and open the host projector in a new tab. Every enrolled student's dashboard
// poll picks up the ambush and pushes them into the play screen.
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Zap, Loader2, ChevronRight, Sparkles, ShieldAlert } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { listClassrooms, startPopQuiz } from "@/lib/api";
import { toast } from "sonner";

export const PopQuizButton = ({ lecture, currentSlideIndex = 0 }) => {
  const { user } = useAuth();
  const isInstructor = user && (user.role === "instructor" || user.role === "admin");
  const [open, setOpen] = useState(false);
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isInstructor || !open) return;
    listClassrooms().then((rows) => {
      setClasses(rows || []);
      if (rows && rows.length && !classId) setClassId(rows[0].id);
    }).catch(() => setClasses([]));
  }, [isInstructor, open]); // eslint-disable-line

  if (!isInstructor) return null;

  const fire = async () => {
    if (!classId) return toast.error("Pick a class first");
    setLoading(true);
    try {
      // Send the 5 most recent slide indices as context
      const total = (lecture.slides || []).length;
      const to = Math.max(0, Math.min(total - 1, currentSlideIndex));
      const indices = [];
      for (let i = Math.max(0, to - 4); i <= to; i++) indices.push(i);
      const g = await startPopQuiz(classId, lecture.id, indices);
      toast.success(`POP QUIZ launched — every student in this class just got buzzed 🔔`);
      // Open host projector view in new tab so instructor can see live results
      window.open(`/game/host/${g.id}`, "_blank");
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't launch pop-quiz");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <motion.button
        data-testid="pop-quiz-btn"
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.97 }}
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-rose-500 to-orange-500 text-white px-4 py-2 text-sm font-black uppercase tracking-widest shadow-lg hover:from-rose-600 hover:to-orange-600 transition"
        title="Instantly buzz every student's phone with a 30-sec question from the last 5 slides"
      >
        <Zap className="h-4 w-4 fill-current" /> Pop Quiz
      </motion.button>

      <Dialog open={open} onOpenChange={(o) => !o && !loading && setOpen(false)}>
        <DialogContent className="max-w-md p-0 overflow-hidden">
          <DialogTitle className="sr-only">Launch a pop-quiz ambush</DialogTitle>
          <DialogDescription className="sr-only">Send an instant 30-second question to every student in a class.</DialogDescription>
          <div className="bg-gradient-to-br from-rose-500 to-orange-500 text-white p-6">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/15 border border-white/25 px-3 py-1 text-[10px] font-bold uppercase tracking-widest">
              <Sparkles className="h-3 w-3" /> Instructor tool
            </div>
            <h2 className="mt-3 font-serif text-3xl font-black">POP QUIZ NOW</h2>
            <p className="mt-1 text-sm text-white/85">Every student in the class gets buzzed on their phone with a 30-second question drawn from your last 5 slides.</p>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Target class</label>
              {classes.length === 0 ? (
                <div className="mt-2 rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground text-center">
                  You don't have any classes yet — create one on the Classes tab first.
                </div>
              ) : (
                <select
                  data-testid="pop-quiz-class-select"
                  value={classId}
                  onChange={(e) => setClassId(e.target.value)}
                  className="mt-2 w-full rounded-xl border border-input bg-background px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-ring/60 transition cursor-pointer"
                >
                  {classes.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}{c.subject ? ` · ${c.subject}` : ""}</option>
                  ))}
                </select>
              )}
            </div>
            <div className="rounded-xl bg-secondary/40 p-3 flex items-start gap-2 text-xs text-muted-foreground">
              <ShieldAlert className="h-3.5 w-3.5 text-accent shrink-0 mt-0.5" />
              <span>Fair-play: the question is auto-generated from slides {Math.max(1, currentSlideIndex - 3)}–{currentSlideIndex + 1} of THIS lecture, so students who've been paying attention have the edge.</span>
            </div>
            <button
              data-testid="pop-quiz-launch"
              disabled={loading || !classId || classes.length === 0}
              onClick={fire}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-rose-500 to-orange-500 text-white px-6 py-3.5 font-black text-base uppercase tracking-widest shadow-lg hover:opacity-90 disabled:opacity-50 transition"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Fire it <ChevronRight className="h-5 w-5" /></>}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PopQuizButton;
